export interface DwtPayloadValidationMessage {
    readonly key: string;
    readonly message: string;
}

export interface DwtPayloadValidationResult {
    readonly warnings: readonly DwtPayloadValidationMessage[];
    readonly errors: readonly DwtPayloadValidationMessage[];
    readonly message: string | null;
}

type ValidationEnvelope = {
    validation?: {
        errors?: unknown;
    };
};

function isValidationMessage(value: unknown): value is DwtPayloadValidationMessage {
    if (value === null || typeof value !== 'object') {
        return false;
    }
    const record = value as { key?: unknown; message?: unknown };
    return typeof record.key === 'string' && typeof record.message === 'string';
}

function parseValidationMessages(value: unknown): DwtPayloadValidationMessage[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isValidationMessage);
}

function readValidationMessagesFromEnvelope(envelope: unknown): DwtPayloadValidationMessage[] {
    if (envelope === null || typeof envelope !== 'object') {
        return [];
    }
    const validation = (envelope as ValidationEnvelope).validation;
    return parseValidationMessages(validation?.errors);
}

/**
 * Mirrors web RWR parsing: warnings live on `responsePayload.data.validation.errors`,
 * blocking errors on `responsePayload.errors.validation.errors`.
 */
export function parseDwtPayloadResponsePayload(responsePayload: unknown): DwtPayloadValidationResult {
    if (responsePayload === null || typeof responsePayload !== 'object') {
        return { warnings: [], errors: [], message: null };
    }

    const record = responsePayload as Record<string, unknown>;
    return {
        warnings: readValidationMessagesFromEnvelope(record['data']),
        errors: readValidationMessagesFromEnvelope(record['errors']),
        message: typeof record['message'] === 'string' ? record['message'] : null,
    };
}

export function formatDwtPayloadErrorsOrWarnings(result: DwtPayloadValidationResult): string {
    const parts: string[] = [];
    if (result.message) {
        parts.push(result.message);
    }
    for (const error of result.errors) {
        parts.push(`Error: ${error.message}`);
    }
    for (const warning of result.warnings) {
        parts.push(`Warning: ${warning.message}`);
    }
    return parts.join('\n');
}
